import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, EyeOff, Eye, Tag as TagIcon } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PageHeader } from '@/components/ui/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { TableSkeleton } from '@/components/ui/skeleton'
import { useTags, useDeleteTag, useSetTagArchived, fetchTagTxCount, type Tag } from '@/hooks/useTags'
import { useHousehold } from '@/context/HouseholdContext'
import { useConfirm } from '@/context/ConfirmProvider'
import { TagFormDialog } from '@/components/tags/TagFormDialog'
import { useT } from '@/i18n/I18nProvider'
import { cn } from '@/lib/utils'

export function TagsPage() {
  const { t } = useT()
  const { householdId } = useHousehold()
  const { data: tags, isLoading } = useTags(true)
  const del = useDeleteTag()
  const setArchived = useSetTagArchived()
  const confirm = useConfirm()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Tag | null>(null)
  const [counts, setCounts] = useState<Record<string, number>>({})

  // Quantidade de transações por tag (para exibir e avisar na exclusão).
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const out: Record<string, number> = {}
      for (const tag of tags ?? []) {
        try {
          out[tag.id] = await fetchTagTxCount(householdId, tag.name)
        } catch {
          out[tag.id] = 0
        }
      }
      if (!cancelled) setCounts(out)
    })()
    return () => {
      cancelled = true
    }
  }, [tags, householdId])

  function openNew() {
    setEditing(null)
    setDialogOpen(true)
  }
  function openEdit(tag: Tag) {
    setEditing(tag)
    setDialogOpen(true)
  }
  async function toggleArchived(tag: Tag) {
    try {
      await setArchived.mutateAsync({ id: tag.id, archived: !tag.archived })
      toast.success(tag.archived ? t('mng.tag.enabledToast') : t('mng.tag.disabledToast'))
    } catch (e) {
      toast.error(t('mng.errChange'), { description: (e as Error).message })
    }
  }
  async function remove(tag: Tag) {
    const n = counts[tag.id] ?? 0
    const ok = await confirm({
      title: t('mng.deleteTitle', { name: tag.name }),
      description: n > 0 ? t('mng.tag.delWarn', { count: n }) : t('mng.tag.delNoTx'),
      confirmText: t('common.delete'),
      tone: 'destructive',
    })
    if (!ok) return
    try {
      await del.mutateAsync({ id: tag.id, name: tag.name })
      toast.success(t('mng.tag.deleted'))
    } catch (e) {
      toast.error(t('mng.errDelete'), { description: (e as Error).message })
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={t('settings.tags')}
        actions={
          <Button onClick={openNew}>
            <Plus className="size-4" /> {t('mng.tag.new')}
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <TableSkeleton rows={6} cols={4} />
          ) : (tags ?? []).length === 0 ? (
            <EmptyState
              icon={TagIcon}
              title={t('mng.tag.emptyTitle')}
              description={t('mng.tag.emptyDesc')}
              action={
                <Button onClick={openNew}>
                  <Plus className="size-4" /> {t('mng.tag.new')}
                </Button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('mng.tag.colTag')}</TableHead>
                    <TableHead>{t('mng.tag.colDesc')}</TableHead>
                    <TableHead className="text-right">{t('mng.tag.colTx')}</TableHead>
                    <TableHead className="w-32" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(tags ?? []).map((tag) => (
                    <TableRow key={tag.id} className={cn(tag.archived && 'opacity-60')}>
                      <TableCell className="font-medium">
                        <span className="inline-flex items-center gap-2">
                          <TagIcon className="text-primary size-3.5 shrink-0" />
                          {tag.name}
                          {tag.archived && (
                            <Badge variant="outline" className="text-muted-foreground text-xs">
                              {t('mng.disabledBadge')}
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{tag.description ?? '—'}</TableCell>
                      <TableCell className="text-right tabular-nums">{counts[tag.id] ?? '—'}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => toggleArchived(tag)}
                            aria-label={tag.archived ? t('mng.enable') : t('mng.disable')}
                            title={tag.archived ? t('mng.enable') : t('mng.disable')}
                          >
                            {tag.archived ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(tag)} aria-label={t('common.edit')}>
                            <Pencil className="size-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => remove(tag)} aria-label={t('common.delete')}>
                            <Trash2 className="text-destructive size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <TagFormDialog open={dialogOpen} onOpenChange={setDialogOpen} tag={editing} />
    </div>
  )
}
